import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Modal } from "newui/components/ui/Modal";
import { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { CheckDuplicatesResult } from "../types/rfx-document.types";
import { InlineEditableCell } from "./InlineEditableCell";

export interface BatchResolution {
  action: "replace" | "keep";
  /** Existing document id this resolution targets. Required so the consumer
   * knows what to delete when the user picked "replace". */
  existingId: number;
  fileName: string;
  /** Category chosen for the incoming file (sent on the upload). Same value
   * is repeated across all resolutions sharing the same fileName. */
  incomingCategoryId?: number;
}

interface BatchDuplicateResolutionModalProps {
  categoryOptions: SelectOption[];
  /** Default category id chosen in the upload section. Used as the initial
   * value for every incoming file's category. */
  defaultCategoryId: number | null;
  duplicates: CheckDuplicatesResult["duplicates"];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (resolutions: BatchResolution[]) => void;
}

interface GroupedExisting {
  action: "replace" | "keep";
  categoryName?: string;
  existingId: number;
  revision?: string;
}

interface IncomingGroup {
  categoryId: number | null;
  existings: GroupedExisting[];
  fileName: string;
}

export const BatchDuplicateResolutionModal = ({
  isOpen,
  duplicates,
  categoryOptions,
  defaultCategoryId,
  onConfirm,
  onClose,
}: BatchDuplicateResolutionModalProps) => {
  // Group existing matches by the incoming filename they conflict with. Each
  // group represents ONE new file the user dropped; the rows beneath it are
  // the existing documents that share that filename. Seed via useState lazy
  // initializer so the grouping runs once on mount; subsequent edits live in
  // state (so we don't want the props to re-derive it).
  const [groups, setGroups] = useState<Record<string, IncomingGroup>>(() => {
    const seed: Record<string, IncomingGroup> = {};
    duplicates.forEach((d) => {
      if (!seed[d.fileName]) {
        seed[d.fileName] = {
          fileName: d.fileName,
          categoryId: defaultCategoryId,
          existings: [],
        };
      }
      const categoryLabel = categoryOptions.find(
        (o) => Number(o.value) === d.existing.categoryId
      )?.label as string | undefined;
      seed[d.fileName].existings.push({
        existingId: d.existing.id,
        categoryName: categoryLabel,
        revision: d.existing.externalRevision,
        action: "replace",
      });
    });
    return seed;
  });

  const updateGroup = (fileName: string, patch: Partial<IncomingGroup>) => {
    setGroups((prev) => ({
      ...prev,
      [fileName]: { ...prev[fileName], ...patch },
    }));
  };

  const updateExisting = (
    fileName: string,
    existingId: number,
    action: "replace" | "keep"
  ) => {
    setGroups((prev) => {
      const group = prev[fileName];
      return {
        ...prev,
        [fileName]: {
          ...group,
          existings: group.existings.map((e) =>
            e.existingId === existingId ? { ...e, action } : e
          ),
        },
      };
    });
  };

  const handleConfirm = () => {
    const resolutions: BatchResolution[] = [];
    Object.values(groups).forEach((group) => {
      group.existings.forEach((e) => {
        resolutions.push({
          fileName: group.fileName,
          existingId: e.existingId,
          action: e.action,
          incomingCategoryId: group.categoryId ?? undefined,
        });
      });
    });
    onConfirm(resolutions);
  };

  const groupList = Object.values(groups);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${groupList.length} Duplicate Filename${groupList.length === 1 ? "" : "s"} Detected`}
      modalType="standard"
      size="xl"
      description={
        <Text mb={2}>
          For each new file, choose what to do with the existing document(s) that
          share its name. Select the category the new file should be uploaded
          under.
        </Text>
      }
      primaryButton={{
        text: "Confirm All",
        btntype: "primary",
        onClick: handleConfirm,
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: onClose,
      }}
    >
      <Stack spacing={5}>
        {groupList.map((group) => (
          <Box
            key={group.fileName}
            borderWidth="1px"
            borderRadius="md"
            p={4}
            data-testid={`incoming-group-${group.fileName}`}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
              gap={4}
              flexWrap="wrap"
            >
              <Box>
                <Badge colorScheme="green" mb={1}>
                  New file
                </Badge>
                <Text fontWeight="semibold" fontSize="sm">
                  {group.fileName}
                </Text>
              </Box>
              <Box minW="220px">
                <Text fontSize="xs" color="gray.600" mb={1}>
                  Category for new file
                </Text>
                <InlineEditableCell
                  mode="select"
                  value={group.categoryId}
                  options={categoryOptions}
                  onSave={(v) =>
                    updateGroup(group.fileName, { categoryId: Number(v) })
                  }
                  testId={`incoming-category-${group.fileName}`}
                />
              </Box>
            </Box>

            <Text fontSize="xs" color="gray.600" mb={2}>
              Existing document{group.existings.length === 1 ? "" : "s"} with this
              filename:
            </Text>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Existing category</Th>
                  <Th>Revision</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {group.existings.map((e) => (
                  <Tr key={e.existingId} data-testid={`batch-row-${e.existingId}`}>
                    <Td>
                      <Text fontSize="sm">{e.categoryName ?? "—"}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">{e.revision ?? "—"}</Text>
                    </Td>
                    <Td>
                      <Box display="flex" gap={1}>
                        <Button
                          size="xs"
                          colorScheme={e.action === "replace" ? "blue" : "gray"}
                          onClick={() =>
                            updateExisting(group.fileName, e.existingId, "replace")
                          }
                          data-testid={`batch-replace-${e.existingId}`}
                        >
                          Replace (delete)
                        </Button>
                        <Button
                          size="xs"
                          colorScheme={e.action === "keep" ? "blue" : "gray"}
                          onClick={() =>
                            updateExisting(group.fileName, e.existingId, "keep")
                          }
                          data-testid={`batch-keep-${e.existingId}`}
                        >
                          Keep
                        </Button>
                      </Box>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ))}
      </Stack>
    </Modal>
  );
};
